import { Injectable, inject } from '@angular/core';
import { MatomoTracker } from '@ngx-matomo/tracker';
import { Logger } from 'shared';
import { MatomoConfig, CustomDimension } from '../models/analytics.models';

/**
 * MatomoService
 *
 * Core service for Matomo analytics initialization and configuration.
 * Handles consent management, user identification, and custom dimensions.
 */
@Injectable({
  providedIn: 'root'
})
export class MatomoService {
  protected logger = new Logger('MatomoService');
  private readonly tracker = inject(MatomoTracker);
  private config?: MatomoConfig;
  private initialized = false;

  /**
   * Initialize Matomo tracker with configuration
   */
  initialize(config: MatomoConfig): void {
    if (this.initialized) {
      this.logger.warn('MatomoService already initialized');
      return;
    }

    this.config = config;

    if (!config.enabled) {
      this.logger.log('Matomo tracking is disabled');
      return;
    }

    try {
      // Configure heartbeat timer if enabled
      if (config.enableHeartBeatTimer && config.heartBeatTimerInterval) {
        this.tracker.enableHeartBeatTimer(config.heartBeatTimerInterval);
      }

      // Configure link tracking
      if (config.enableLinkTracking) {
        this.tracker.enableLinkTracking();
      }

      this.initialized = true;
      this.logger.log('Matomo analytics initialized', {
        siteId: config.siteId,
        requireConsent: config.requireConsent
      });
    } catch (error) {
      this.logger.error('Failed to initialize Matomo:', error);
    }
  }

  /**
   * Require consent before tracking
   * Users must explicitly consent before any tracking occurs
   */
  requireConsent(): void {
    if (!this.isEnabled()) return;
    this.tracker.requireConsent();
  }

  /**
   * Remember that user has given consent
   * @param hours Number of hours to remember consent (default: 365 days)
   */
  rememberConsentGiven(hours: number = 8760): void {
    if (!this.isEnabled()) return;
    this.tracker.rememberConsentGiven(hours);
    this.logger.log(`Matomo consent granted for ${hours} hours`);
  }

  /**
   * Forget that user has given consent
   * User will need to consent again
   */
  forgetConsentGiven(): void {
    if (!this.isEnabled()) return;
    this.tracker.forgetConsentGiven();
    this.logger.log('Matomo consent revoked');
  }

  /**
   * Set user ID for tracking authenticated users
   * @param userId User identifier (numeric ID recommended, not email)
   */
  setUserId(userId: string | number): void {
    if (!this.isEnabled()) return;
    this.tracker.setUserId(userId.toString());
    this.logger.log('Matomo user ID set:', userId);
  }

  /**
   * Reset user ID (call on logout)
   */
  resetUserId(): void {
    if (!this.isEnabled()) return;
    this.tracker.resetUserId();
    this.logger.log('Matomo user ID reset');
  }

  /**
   * Set custom dimension value
   * @param dimensionId Custom dimension ID (configured in Matomo)
   * @param value Dimension value
   */
  setCustomDimension(dimensionId: number, value: string): void {
    if (!this.isEnabled()) return;
    this.tracker.setCustomDimension(dimensionId, value);
    this.logger.log(`Matomo custom dimension ${dimensionId} set:`, value);
  }

  /**
   * Delete custom dimension
   * @param dimensionId Custom dimension ID
   */
  deleteCustomDimension(dimensionId: number): void {
    if (!this.isEnabled()) return;
    this.tracker.deleteCustomDimension(dimensionId);
    this.logger.log(`Matomo custom dimension ${dimensionId} deleted`);
  }

  /**
   * Set multiple custom dimensions at once
   * @param dimensions Array of custom dimensions
   */
  setCustomDimensions(dimensions: CustomDimension[]): void {
    if (!this.isEnabled()) return;
    dimensions.forEach(dim => {
      this.setCustomDimension(dim.id, dim.value);
    });
  }

  /**
   * Enable Do Not Track detection
   */
  setDoNotTrack(enable: boolean): void {
    if (!this.isEnabled()) return;
    this.tracker.setDoNotTrack(enable);
  }

  /**
   * Disable cookies (for cookie-less tracking)
   */
  disableCookies(): void {
    if (!this.isEnabled()) return;
    this.tracker.disableCookies();
    this.logger.log('Matomo cookies disabled');
  }

  /**
   * Set cookie domain
   * @param domain Cookie domain (e.g., '*.example.com')
   */
  setCookieDomain(domain: string): void {
    if (!this.isEnabled()) return;
    this.tracker.setCookieDomain(domain);
  }

  /**
   * Set referrer URL manually
   * @param referrerUrl Referrer URL
   */
  setReferrerUrl(referrerUrl: string): void {
    if (!this.isEnabled()) return;
    this.tracker.setReferrerUrl(referrerUrl);
  }

  /**
   * Check if Matomo is enabled
   */
  isEnabled(): boolean {
    return this.config?.enabled ?? false;
  }

  /**
   * Check if Matomo is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get current configuration
   */
  getConfig(): MatomoConfig | undefined {
    return this.config;
  }
}
